import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Reveal } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";
import { api } from "../lib/api";

const TITLES = {
  tous: "boutique.allTitle",
  homme: "nav.man",
  femme: "nav.woman",
  couple: "nav.couple",
  enfants: "nav.kids",
  maroquinerie: "footer.catLeather",
};

export default function Boutique() {
  const [params, setParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const category = params.get("categorie") || "tous";
  const sous = params.get("sous") || "";
  const [sort, setSort] = useState("nouveautes");
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, [i18n.language]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: { category, sous, sort } })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, sous, sort, i18n.language]);

  const catLabel = (key) => {
    const c = cats.find((x) => x.key === key);
    if (!c) return t(TITLES[key] || "boutique.allTitle");
    return c.labelKey && i18n.exists(c.labelKey) ? t(c.labelKey) : c.label;
  };

  const subs = category !== "tous" ? [...new Set(products.map((p) => p.sub).filter(Boolean))].sort() : [];

  const setCategory = (key) => setParams(key === "tous" ? {} : { categorie: key });
  const setSub = (s) => {
    const next = { categorie: category };
    if (s !== sous) next.sous = s;
    setParams(next);
  };

  const title = category === "tous" ? t("boutique.allTitle") : catLabel(category);

  return (
    <main data-testid="boutique-page" className="mx-auto max-w-[1500px] px-6 pb-24 pt-36 lg:px-12 lg:pt-44">
      <Reveal>
        <p className="eyebrow">{t("boutique.eyebrow")}</p>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
          <h1 className="font-serif text-4xl uppercase tracking-tight text-ivoire sm:text-5xl lg:text-6xl">
            {category === "tous" ? <><span className="italic normal-case text-or">{t("boutique.allPre")}</span> {t("boutique.allPost")}</> : title}
          </h1>
          <p data-testid="boutique-count" className="font-mono text-[11px] uppercase tracking-[0.25em] text-argile">
            {loading
              ? t("common.loading")
              : products.length > 1
                ? t("common.piecesN", { n: products.length })
                : t("common.pieces1", { n: products.length })}
          </p>
        </div>
      </Reveal>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-6 border-y border-ligne py-5">
        <div className="flex flex-wrap gap-2">
          <button
            key="tous"
            data-testid="boutique-filter-tous"
            onClick={() => setCategory("tous")}
            className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-all duration-300 ${
              category === "tous"
                ? "border-or bg-or text-noir"
                : "border-ligne text-argile hover:border-or hover:text-or"
            }`}
          >
            {t("boutique.filterAll")}
          </button>
          {cats.map((c) => (
            <button
              key={c.key}
              data-testid={`boutique-filter-${c.key}`}
              onClick={() => setCategory(c.key)}
              className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-all duration-300 ${
                category === c.key
                  ? "border-or bg-or text-noir"
                  : "border-ligne text-argile hover:border-or hover:text-or"
              }`}
            >
              {catLabel(c.key)}
            </button>
          ))}
        </div>
        <select
          data-testid="boutique-sort-select"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="border border-ligne bg-card2 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-argile focus:border-or"
        >
          <option value="nouveautes">{t("boutique.sortNew")}</option>
          <option value="prix_asc">{t("boutique.sortAsc")}</option>
          <option value="prix_desc">{t("boutique.sortDesc")}</option>
        </select>
      </div>

      {subs.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="boutique-subfilters">
          <span className="me-2 font-mono text-[9px] uppercase tracking-[0.25em] text-argile/70">{t("boutique.families")}</span>
          {subs.map((s) => (
            <button
              key={s}
              data-testid={`boutique-subfilter-${s.replace(/\s/g, "-")}`}
              onClick={() => setSub(s)}
              className={`border px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] transition-all duration-300 ${
                sous === s
                  ? "border-or text-or"
                  : "border-ligne text-argile/70 hover:border-or hover:text-or"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
      </div>

      {!loading && products.length === 0 && (
        <p className="py-24 text-center text-sm text-argile">{t("boutique.empty")}</p>
      )}
    </main>
  );
}
